import React, { useState, useMemo } from "react";
import { useTableData } from "../../hooks/useTableData";
import { dayWiseUserReportConfig } from "config/tableConfigs";
import { useSession } from "hooks/useSession";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// ─────────────────────────────────────────────────────────────────────────────
// Column keys per role — detail rows
// ─────────────────────────────────────────────────────────────────────────────
const DETAIL_COLUMNS = {
  Retailer: [
    "id",
    "txn_date",
    "txn_time",
    "bc_name",
    "bc_phone",
    "service",
    "txn_flow",
    "amount",
    "status",
    "Opening Balance BC",
    "Closing Balance BC",
    "ref_no",
    "ack_no",
    "remark",
    "created_at"
  ],
  "API Partner": [
    "id",
    "txn_date",
    "txn_time",
    "bc_name",
    "bc_phone",
    "service",
    "txn_flow",
    "amount",
    "status",
    "Opening Balance BC",
    "Closing Balance BC",
    "ref_no",
    "ack_no",
    "api_txn_id",
    "created_at"
  ],
  Distributer: [
    "id",
    "txn_date",
    "txn_time",
    "bc_name",
    "bc_phone",
    "service",
    "txn_flow",
    "amount",
    "status",
    "Opening Balance Dist",
    "Closing Balance Dist",
    "Opening Balance BC",
    "Closing Balance BC",
    "ref_no",
    "ack_no",
    "created_at"
  ],
  "Master Distributor": [
    "id",
    "txn_date",
    "txn_time",
    "bc_name",
    "bc_phone",
    "service",
    "txn_flow",
    "amount",
    "status",
    "Opening Balance MD",
    "Closing Balance MD",
    "Opening Balance Dist",
    "Closing Balance Dist",
    "Opening Balance BC",
    "Closing Balance BC",
    "ref_no",
    "ack_no",
    "created_at"
  ],
  "White label": [
    "id",
    "txn_date",
    "txn_time",
    "bc_name",
    "bc_phone",
    "service",
    "txn_flow",
    "amount",
    "status",
    "Opening Balance WL",
    "Closing Balance WL",
    "Opening Balance MD",
    "Closing Balance MD",
    "Opening Balance Dist",
    "Closing Balance Dist",
    "Opening Balance BC",
    "Closing Balance BC",
    "ref_no",
    "ack_no",
    "created_at"
  ],
  Admin: [
    "id",
    "txn_date",
    "txn_time",
    "bc_name",
    "bc_phone",
    "service",
    "txn_flow",
    "amount",
    "status",
    "Opening Balance Admin",
    "Closing Balance Admin",
    "Opening Balance WL",
    "Closing Balance WL",
    "Opening Balance MD",
    "Closing Balance MD",
    "Opening Balance Dist",
    "Closing Balance Dist",
    "Opening Balance BC",
    "Closing Balance BC",
    "ref_no",
    "ack_no",
    "created_at"
  ],
  "Main Admin": null
};

// ─────────────────────────────────────────────────────────────────────────────
// Column keys per role — day summary view
// ─────────────────────────────────────────────────────────────────────────────
const SUMMARY_COLUMNS = {
  Retailer: [
    "txn_date",
    "bc_name",
    "bc_phone",
    "service",
    "total_txn",
    "total_amount"
  ],
  "API Partner": [
    "txn_date",
    "bc_name",
    "bc_phone",
    "service",
    "total_txn",
    "total_amount"
  ],
  Distributer: [
    "txn_date",
    "bc_name",
    "bc_phone",
    "service",
    "total_txn",
    "total_amount"
  ],
  "Master Distributor": [
    "txn_date",
    "bc_name",
    "bc_phone",
    "service",
    "total_txn",
    "total_amount"
  ],
  "White label": [
    "txn_date",
    "bc_name",
    "bc_phone",
    "service",
    "total_txn",
    "total_amount"
  ],
  Admin: [
    "txn_date",
    "bc_id",
    "bc_name",
    "bc_phone",
    "service",
    "total_txn",
    "total_amount"
  ],
  "Main Admin": null
};

const SERVICE_TABS = [
  { key: "all", label: "All" },
  { key: "fund_transfer", label: "Fund Transfer" },
  { key: "credit_debit", label: "Credit / Debit" }
];

const VIEW_TABS = [
  { key: "detail", label: "Detail" },
  { key: "summary", label: "Day Summary" },
  { key: "service", label: "By Service" }
];

const buildUrl = (userid, start, end, service = "") => {
  let url = `https://pro.mokshiq.in/day_wise_user_report?user=${userid}&filter_date=${start}&end_date=${end}`;
  if (service && service !== "all") url += `&service=${service}`;
  return url;
};

const DayWiseUserReportPage = () => {
  const { userid, role } = useSession();
  const userRole = role === "Platform User" ? "Admin" : role;
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [activeService, setActiveService] = useState("all");
  const [activeView, setActiveView] = useState("detail");

  const getTodayRange = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const local = new Date(now.getTime() - offset * 60 * 1000);
    const today = local.toISOString().split("T")[0];
    return { start: today, end: today };
  };

  const { start, end } = getTodayRange();

  const [startDate, setStartDate] = useState(start);
  const [endDate, setEndDate] = useState(end);

  const [apiUrl, setApiUrl] = useState(() => buildUrl(userid, start, end));

  const tableData = useTableData(apiUrl, dayWiseUserReportConfig, refreshKey);

  const {
    loading: tableLoading,
    error: tableError,
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    sortField,
    sortDirection,
    handleSort,
    selectedRows,
    handleRowSelect,
    handleSelectAll,
    filters,
    handleFilterChange,
    paginatedData,
    sortedData,
    totalPages,
    data
  } = tableData;

  const handleDateSubmit = (e) => {
    if (e) e.preventDefault();
    const newUrl = buildUrl(userid, startDate, endDate, activeService);
    setApiUrl(newUrl);
    setRefreshKey((prev) => prev + 1);
  };

  const handleServiceChange = (svc) => {
    setActiveService(svc);
    const newUrl = buildUrl(userid, startDate, endDate, svc);
    setApiUrl(newUrl);
    setRefreshKey((prev) => prev + 1);
    setCurrentPage(1);
  };

  // Pick correct array based on active view
  const activeViewData = useMemo(() => {
    if (!sortedData && !tableData?.rawResponse) return [];

    let rows;
    if (activeView === "summary") {
      rows = tableData?.rawResponse?.day_wise_summary || [];
    } else if (activeView === "service") {
      rows = tableData?.rawResponse?.service_totals || [];
    } else {
      rows = sortedData || [];
    }

    if (activeView === "detail" && activeService && activeService !== "all") {
      rows = rows.filter((r) =>
        activeService === "fund_transfer"
          ? r.service === "fund_transfer" || r.service === "fund_trasfer"
          : r.service === activeService
      );
    }

    return rows;
  }, [sortedData, tableData, activeView, activeService]);

  const displayPaginatedData = useMemo(() => {
    if (!activeViewData) return [];
    const startIndex = (currentPage - 1) * itemsPerPage;
    return activeViewData.slice(startIndex, startIndex + itemsPerPage);
  }, [activeViewData, currentPage, itemsPerPage]);

  const displayTotalPages = useMemo(() => {
    return Math.ceil((activeViewData?.length || 0) / itemsPerPage);
  }, [activeViewData, itemsPerPage]);

  // Filter columns by role + view
  const filteredConfig = useMemo(() => {
    const isMainAdmin = DETAIL_COLUMNS[userRole] === null;

    let keys;
    if (activeView === "summary") {
      keys = isMainAdmin ? null : SUMMARY_COLUMNS[userRole] || [];
    } else if (activeView === "service") {
      keys = ["service", "total_txn", "total_amount"];
    } else {
      keys = isMainAdmin ? null : DETAIL_COLUMNS[userRole] || [];
    }

    if (keys === null) return dayWiseUserReportConfig;
    if (!keys.length) return { ...dayWiseUserReportConfig, columns: [] };

    const allowedSet = new Set(keys);
    return {
      ...dayWiseUserReportConfig,
      columns: dayWiseUserReportConfig.columns.filter((col) =>
        allowedSet.has(col.key)
      )
    };
  }, [userRole, activeView]);

  // Count rows per product
  const serviceCounts = useMemo(() => {
    const rows = data || [];
    return {
      all: rows.length,
      fund_transfer: rows.filter(
        (r) => r.service === "fund_transfer" || r.service === "fund_trasfer"
      ).length,
      credit_debit: rows.filter((r) => r.service === "credit_debit").length
    };
  }, [data]);

  const grandTotals = tableData?.rawResponse?.grand_totals || {};

  const handleExport = (type = "excel") => {
    try {
      let exportData =
        selectedRows.length > 0
          ? activeViewData.filter((item) =>
              selectedRows.includes(item.id || item[filteredConfig.idField])
            )
          : activeViewData;

      if (!exportData || exportData.length === 0) {
        alert("No data available to export");
        return;
      }

      const formattedData = exportData.map((row) => {
        const obj = {};
        filteredConfig.columns.forEach((col) => {
          obj[col.label] = row[col.key];
        });
        return obj;
      });

      if (type === "excel") {
        const ws = XLSX.utils.json_to_sheet(formattedData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Day Wise Reports");
        const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        saveAs(
          new Blob([buffer]),
          `Day_Wise_Reports_${startDate}_to_${endDate}.xlsx`
        );
      }
    } catch (err) {
      console.error("Export error:", err);
      alert("Export failed");
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === "") return "N/A";
    const num = parseFloat(amount);
    if (isNaN(num)) return "N/A";
    return `₹${num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatCellValue = (value, column) => {
    if (value === null || value === undefined || value === "") return "N/A";
    if (column.type === "currency") return formatCurrency(value);
    if (column.type === "badge") {
      const bgClass = column.badgeClass?.(value) || "bg-secondary";
      return (
        <span
          className={`badge ${bgClass} rounded-pill px-2.5 py-1 fw-semibold`}
          style={{ fontSize: "11px" }}
        >
          {column.transform
            ? column.transform(value)
            : String(value).toUpperCase()}
        </span>
      );
    }
    return column.transform ? column.transform(value) : String(value);
  };

  const totalRecordsCount = activeViewData?.length || 0;
  const startItem =
    totalRecordsCount > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalRecordsCount);

  return (
    <main>
      <div className="container-fluid">
        {(loading || tableLoading) && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
            style={{ backgroundColor: "rgba(255, 255, 255, 0.75)", zIndex: 4000 }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {/* 1. Hero Header Banner with Integrated Date Filter Bar */}
        <div
          className="card border-0 mb-3 overflow-hidden position-relative shadow-sm"
          style={{
            background: "linear-gradient(135deg, #f5efff 0%, #e0e7ff 100%)",
            borderRadius: "14px"
          }}
        >
          <div className="card-body p-3.5 position-relative z-1">
            {/* Header Info */}
            <div className="row align-items-center mb-3">
              <div className="col-12 col-md-8">
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="d-flex align-items-center justify-content-center flex-shrink-0 shadow-xs"
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "12px",
                      backgroundColor: "#7e57c2",
                      color: "#ffffff"
                    }}
                  >
                    <i
                      className="ph-duotone ph-calendar-check"
                      style={{ fontSize: "20px" }}
                    ></i>
                  </div>
                  <div>
                    <h5
                      className="fw-extrabold mb-0.5"
                      style={{ color: "#0f172a", fontSize: "17px" }}
                    >
                      Day Wise User Reports
                    </h5>
                    <p className="text-muted mb-0" style={{ fontSize: "12px" }}>
                      Analyze daily service performance, summary breakdown, and user transaction activity.
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-4 text-end d-none d-md-block">
                <img
                  src="/assets/images/day_wise_report_3d.png"
                  alt="Day Wise Reports 3D Illustration"
                  style={{
                    maxHeight: "65px",
                    objectFit: "contain"
                  }}
                />
              </div>
            </div>

            {/* Integrated Filter Bar inside Header Card */}
            <div className="pt-1">
              <form onSubmit={handleDateSubmit}>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <div
                      className="d-flex flex-column"
                      style={{ width: "165px" }}
                    >
                      <label
                        className="form-label text-secondary fw-semibold mb-1"
                        style={{ fontSize: "11px" }}
                      >
                        From Date
                      </label>
                      <div className="position-relative">
                        <i
                          className="ph-bold ph-calendar-blank position-absolute top-50 start-0 translate-middle-y ms-2.5"
                          style={{ fontSize: "14px", color: "#7e57c2" }}
                        ></i>
                        <input
                          type="date"
                          className="form-control rounded-3 border-light-subtle shadow-xs ps-4 py-1.5"
                          style={{
                            fontSize: "12.5px",
                            backgroundColor: "#ffffff",
                            color: "#1e293b",
                            height: "36px"
                          }}
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    <div
                      className="d-flex align-items-center justify-content-center text-muted"
                      style={{ marginTop: "18px" }}
                    >
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center shadow-xs"
                        style={{
                          width: "28px",
                          height: "28px",
                          backgroundColor: "#ffffff",
                          color: "#7e57c2"
                        }}
                      >
                        <i
                          className="ph-bold ph-arrow-right"
                          style={{ fontSize: "12px" }}
                        ></i>
                      </div>
                    </div>

                    <div
                      className="d-flex flex-column"
                      style={{ width: "165px" }}
                    >
                      <label
                        className="form-label text-secondary fw-semibold mb-1"
                        style={{ fontSize: "11px" }}
                      >
                        To Date
                      </label>
                      <div className="position-relative">
                        <i
                          className="ph-bold ph-calendar-blank position-absolute top-50 start-0 translate-middle-y ms-2.5"
                          style={{ fontSize: "14px", color: "#7e57c2" }}
                        ></i>
                        <input
                          type="date"
                          className="form-control rounded-3 border-light-subtle shadow-xs ps-4 py-1.5"
                          style={{
                            fontSize: "12.5px",
                            backgroundColor: "#ffffff",
                            color: "#1e293b",
                            height: "36px"
                          }}
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="align-self-end mt-2 mt-sm-0">
                    <button
                      type="submit"
                      className="btn text-white fw-bold d-inline-flex align-items-center justify-content-center gap-2 px-3.5 shadow-xs"
                      style={{
                        background:
                          "linear-gradient(135deg, #7e57c2 0%, #673ab7 100%)",
                        borderRadius: "10px",
                        height: "36px",
                        fontSize: "12.5px"
                      }}
                    >
                      <i
                        className="ph-bold ph-paper-plane-tilt"
                        style={{ fontSize: "14px" }}
                      ></i>
                      Submit
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* 2. Grand Totals Cards */}
        {grandTotals && (
          <div className="d-flex flex-wrap gap-2.5 mb-2">
            <div
              className="card border-0 rounded-4 shadow-sm bg-white position-relative overflow-hidden"
              style={{ padding: "8px 12px", minWidth: "180px", flex: "1 1 180px", maxWidth: "240px" }}
            >
              <div className="d-flex align-items-center position-relative z-1" style={{ gap: "10px" }}>
                <div
                  className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                  style={{
                    width: "32px",
                    height: "32px",
                    backgroundColor: "#f3e8ff",
                    color: "#7e57c2"
                  }}
                >
                  <i
                    className="ph-duotone ph-coins"
                    style={{ fontSize: "16px" }}
                  ></i>
                </div>
                <div className="overflow-hidden min-w-0 flex-grow-1">
                  <span
                    className="text-muted text-uppercase fw-semibold d-block text-truncate"
                    style={{ fontSize: "10px", letterSpacing: "0.03em", marginBottom: "1px" }}
                  >
                    TOTAL TRANSACTIONS
                  </span>
                  <h5
                    className="fw-extrabold text-dark mb-0 text-truncate"
                    style={{ fontSize: "15px", lineHeight: "1.2" }}
                  >
                    {Number(grandTotals.total_txn || 0).toLocaleString("en-IN")}
                  </h5>
                </div>
              </div>

              <div
                className="position-absolute end-0 bottom-0 pe-none"
                style={{
                  color: "#7e57c2",
                  opacity: 0.1,
                  right: "4px",
                  bottom: "2px"
                }}
              >
                <i
                  className="ph-duotone ph-chart-bar"
                  style={{ fontSize: "26px" }}
                ></i>
              </div>
            </div>

            <div
              className="card border-0 rounded-4 shadow-sm bg-white position-relative overflow-hidden"
              style={{ padding: "8px 12px", minWidth: "180px", flex: "1 1 180px", maxWidth: "240px" }}
            >
              <div className="d-flex align-items-center position-relative z-1" style={{ gap: "10px" }}>
                <div
                  className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                  style={{
                    width: "32px",
                    height: "32px",
                    backgroundColor: "#ffe4e6",
                    color: "#f43f5e"
                  }}
                >
                  <i
                    className="ph-bold ph-currency-inr"
                    style={{ fontSize: "16px" }}
                  ></i>
                </div>
                <div className="overflow-hidden min-w-0 flex-grow-1">
                  <span
                    className="text-muted text-uppercase fw-semibold d-block text-truncate"
                    style={{ fontSize: "10px", letterSpacing: "0.03em", marginBottom: "1px" }}
                  >
                    TOTAL AMOUNT
                  </span>
                  <h5
                    className="fw-extrabold text-dark mb-0 text-truncate"
                    style={{ fontSize: "15px", lineHeight: "1.2" }}
                  >
                    {formatCurrency(grandTotals.total_amount || 0)}
                  </h5>
                </div>
              </div>

              <div
                className="position-absolute end-0 bottom-0 pe-none"
                style={{
                  color: "#f43f5e",
                  opacity: 0.1,
                  right: "4px",
                  bottom: "2px"
                }}
              >
                <i
                  className="ph-duotone ph-chart-bar"
                  style={{ fontSize: "26px" }}
                ></i>
              </div>
            </div>
          </div>
        )}

        {/* 4. Controls & Tab Selectors */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2.5 mb-2.5">
          {/* Service Product Chips */}
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {SERVICE_TABS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                className="btn rounded-pill px-3.5 py-1.5 fw-semibold d-inline-flex align-items-center gap-2 shadow-xs text-nowrap"
                style={{
                  fontSize: "13px",
                  whiteSpace: "nowrap",
                  backgroundColor:
                    activeService === key ? "#7e57c2" : "#ffffff",
                  color: activeService === key ? "#ffffff" : "#475569",
                  border:
                    activeService === key
                      ? "1px solid #7e57c2"
                      : "1px solid #cbd5e1",
                  transition: "all 0.15s ease"
                }}
                onClick={() => handleServiceChange(key)}
              >
                <span>{label}</span>
                <span
                  className="badge rounded-pill"
                  style={{
                    backgroundColor:
                      activeService === key
                        ? "rgba(255, 255, 255, 0.25)"
                        : "#e2e8f0",
                    color: activeService === key ? "#ffffff" : "#64748b"
                  }}
                >
                  {serviceCounts[key] ?? 0}
                </span>
              </button>
            ))}
          </div>

          {/* View Selection Tabs with Dividers */}
          <div className="bg-white p-1 rounded-pill shadow-xs border border-light-subtle d-inline-flex align-items-center">
            {VIEW_TABS.map(({ key, label }, idx) => (
              <React.Fragment key={key}>
                {idx > 0 &&
                  activeView !== key &&
                  activeView !== VIEW_TABS[idx - 1].key && (
                    <span
                      className="text-light-subtle px-1"
                      style={{ fontSize: "14px" }}
                    >
                      |
                    </span>
                  )}
                <button
                  type="button"
                  className="btn btn-sm rounded-pill px-3.5 py-1.5 fw-semibold border-0 text-nowrap"
                  style={{
                    fontSize: "12.5px",
                    whiteSpace: "nowrap",
                    backgroundColor:
                      activeView === key ? "#7e57c2" : "transparent",
                    color: activeView === key ? "#ffffff" : "#64748b",
                    transition: "all 0.15s ease"
                  }}
                  onClick={() => {
                    setActiveView(key);
                    setCurrentPage(1);
                  }}
                >
                  {label}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Reports Table Container Card */}
        <div className="card border-0 rounded-4 shadow-sm overflow-hidden mb-4">
          {/* Card Header Matching Design */}
          <div className="card-header bg-white p-3.5 border-bottom border-light-subtle">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div className="d-flex align-items-center">
                <div
                  className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                  style={{
                    width: "38px",
                    height: "38px",
                    backgroundColor: "#f3e8ff",
                    color: "#7e57c2",
                    marginRight: "16px"
                  }}
                >
                  <i
                    className="ph-duotone ph-calendar-check"
                    style={{ fontSize: "20px" }}
                  ></i>
                </div>
                <div>
                  <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: "16px" }}>
                    Day Wise User Reports
                  </h6>
                  <p className="text-muted mb-0" style={{ fontSize: "12.5px" }}>
                    Total {totalRecordsCount} records found
                  </p>
                </div>
              </div>

              {/* Right Header Action Controls */}
              <div className="d-flex align-items-center gap-2 flex-wrap ms-auto">
                <div className="position-relative" style={{ minWidth: "220px" }}>
                  <i
                    className="ph-bold ph-magnifying-glass position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"
                    style={{ fontSize: "14px" }}
                  ></i>
                  <input
                    type="text"
                    className="form-control rounded-pill ps-5 pe-3 py-1.5 border-light-subtle shadow-xs"
                    style={{ fontSize: "13px" }}
                    placeholder="Search by ID, Date, BC Name..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>

                <select
                  className="form-select rounded-3 py-1.5 px-3 border-light-subtle shadow-xs"
                  style={{ width: "75px", fontSize: "13px" }}
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>

                <button
                  type="button"
                  className="btn p-2 rounded-3 text-white d-flex align-items-center justify-content-center"
                  style={{ width: "36px", height: "36px", backgroundColor: "#7e57c2" }}
                  title="Filter Options"
                >
                  <i className="ph-bold ph-funnel" style={{ fontSize: "16px" }}></i>
                </button>

                <button
                  type="button"
                  className="btn p-2 rounded-3 border border-light-subtle d-flex align-items-center justify-content-center"
                  style={{
                    width: "36px",
                    height: "36px",
                    backgroundColor: "#f8fafc",
                    color: "#7e57c2"
                  }}
                  onClick={() => handleExport("excel")}
                  title="Export Excel"
                >
                  <i
                    className="ph-bold ph-download-simple"
                    style={{ fontSize: "16px" }}
                  ></i>
                </button>
              </div>
            </div>
          </div>

          {/* Table Body & Empty State */}
          <div className="card-body p-0">
            <div className="table-responsive app-scroll">
              <table className="table align-middle mb-0 text-nowrap">
                <thead style={{ backgroundColor: "#f5efff", color: "#334155" }}>
                  <tr>
                    <th style={{ width: "40px" }} className="ps-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={
                          selectedRows.length === displayPaginatedData.length &&
                          displayPaginatedData.length > 0
                        }
                        onChange={handleSelectAll}
                      />
                    </th>
                    {filteredConfig.columns.map((column) => (
                      <th
                        key={column.key}
                        onClick={() =>
                          column.sortable !== false && handleSort(column.key)
                        }
                        style={{
                          cursor:
                            column.sortable !== false ? "pointer" : "default",
                          fontSize: "12.5px",
                          fontWeight: "700",
                          color: "#334155",
                          padding: "12px 14px"
                        }}
                      >
                        <div className="d-flex align-items-center gap-1">
                          <span>{column.label}</span>
                          {column.sortable !== false && (
                            <i
                              className="ph-bold ph-caret-up-down text-muted"
                              style={{ fontSize: "11px" }}
                            ></i>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayPaginatedData && displayPaginatedData.length > 0 ? (
                    displayPaginatedData.map((item) => (
                      <tr
                        key={item.id || item[filteredConfig.idField]}
                        className="border-bottom border-light-subtle"
                      >
                        <td className="ps-3">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selectedRows.includes(
                              item.id || item[filteredConfig.idField]
                            )}
                            onChange={() =>
                              handleRowSelect(
                                item.id || item[filteredConfig.idField]
                              )
                            }
                          />
                        </td>
                        {filteredConfig.columns.map((column) => (
                          <td
                            key={column.key}
                            style={{ fontSize: "13px", padding: "12px 14px" }}
                          >
                            {formatCellValue(item[column.key], column)}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : null}
                </tbody>
              </table>

              {/* Empty State Matching Design */}
              {(!displayPaginatedData || displayPaginatedData.length === 0) && (
                <div className="text-center py-5 px-3">
                  <div className="mb-3 d-inline-flex align-items-center justify-content-center position-relative">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle"
                      style={{
                        width: "72px",
                        height: "72px",
                        backgroundColor: "#f3e8ff",
                        color: "#7e57c2"
                      }}
                    >
                      <i
                        className="ph-duotone ph-file-search"
                        style={{ fontSize: "36px" }}
                      ></i>
                    </div>
                  </div>
                  <h6 className="fw-bold text-dark mb-1" style={{ fontSize: "16px" }}>
                    No records found
                  </h6>
                  <p className="text-muted mb-3" style={{ fontSize: "13px" }}>
                    Try adjusting your search or filter criteria
                  </p>
                  <div className="d-flex justify-content-center w-100">
                    <button
                      type="button"
                      className="btn rounded-pill px-3.5 py-1.5 fw-semibold d-inline-flex align-items-center justify-content-center gap-2"
                      style={{
                        border: "1px solid #d8b4fe",
                        backgroundColor: "#fdf4ff",
                        color: "#7e57c2",
                        fontSize: "13px",
                        width: "auto",
                        maxWidth: "180px",
                        display: "inline-flex"
                      }}
                      onClick={() => {
                        setSearchTerm("");
                        setActiveService("all");
                        setActiveView("detail");
                        const { start, end } = getTodayRange();
                        setStartDate(start);
                        setEndDate(end);
                        const newUrl = buildUrl(userid, start, end, "all");
                        setApiUrl(newUrl);
                        setRefreshKey((prev) => prev + 1);
                      }}
                    >
                      <i
                        className="ph-bold ph-arrows-counter-clockwise"
                        style={{ fontSize: "14px" }}
                      ></i>
                      Clear Filters
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Table Footer Matching Design */}
          <div className="card-footer bg-white p-3 border-top border-light-subtle d-flex flex-wrap align-items-center justify-content-between gap-3">
            <span className="text-muted" style={{ fontSize: "13px" }}>
              Showing {startItem} to {endItem} of {totalRecordsCount} entries
            </span>

            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-sm rounded-pill px-3 py-1.5 border border-light-subtle fw-semibold d-inline-flex align-items-center gap-1"
                style={{
                  fontSize: "12.5px",
                  backgroundColor: currentPage === 1 ? "#f8fafc" : "#f1f5f9",
                  color: currentPage === 1 ? "#94a3b8" : "#7e57c2"
                }}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              >
                <i className="ph-bold ph-caret-left" style={{ fontSize: "12px" }}></i>
                Previous
              </button>

              <button
                type="button"
                className="btn btn-sm rounded-pill px-3 py-1.5 border border-light-subtle fw-semibold d-inline-flex align-items-center gap-1"
                style={{
                  fontSize: "12.5px",
                  backgroundColor:
                    currentPage >= displayTotalPages || displayTotalPages === 0
                      ? "#f8fafc"
                      : "#f1f5f9",
                  color:
                    currentPage >= displayTotalPages || displayTotalPages === 0
                      ? "#94a3b8"
                      : "#7e57c2"
                }}
                disabled={currentPage >= displayTotalPages || displayTotalPages === 0}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(displayTotalPages, prev + 1))
                }
              >
                Next
                <i
                  className="ph-bold ph-caret-right"
                  style={{ fontSize: "12px" }}
                ></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default DayWiseUserReportPage;